import React from "react";
import {Button} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {useNavigate} from "react-router-dom";
import {NeighbourhoodSnapshot} from "../../api/Model";
import {useNeighbourhoodSnapshotStore} from "../../api/NeighbourhoodSnapshotStore";
import {useSeatingPlanStore} from "../../api/SeatingPlanStore";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {SeatingPlanFormDialog} from "../seating-plan-list-page/SeatingPlanFormDialog";
import {NeighbourhoodSnapshotTable} from "./NeighbourhoodSnapshotTable";
import {today} from "../../util/DateUtil";
import {usePermission} from "../../util/access/UseAccess";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {AppPages, usePageUrl} from "../../app-layout/AppPages";

export default function NeighbourhoodSnapshotsPage() {
    const {remoteValue, createSnapshot, updateSnapshot, deleteSnapshot} = useNeighbourhoodSnapshotStore();
    const createPlanFromSnapshot = useSeatingPlanStore(state => state.createPlanFromSnapshot);
    const {canAdmin} = usePermission({canAdmin: SeatingPlans_Admin});
    const navigate = useNavigate();
    const pageUrl = usePageUrl();

    const handleCreate = useFormDialogAction({
        title: "Create Neighbourhood Snapshot",
        description: "Capture the current neighbourhood details, desk names, and member assignments.",
        submitButtonText: 'Create Snapshot',
        renderForm: SeatingPlanFormDialog,
        action: openDialog => openDialog({
            initialValue: {name: today(), description: null},
            onSubmit: createSnapshot,
            successMessage: form => `Created snapshot ${form.name}.`,
            errorMessage: 'Failed to create the neighbourhood snapshot. Please try again later.',
        }),
    }, [createSnapshot]);

    const handleUpdate = useFormDialogAction({
        title: "Edit Neighbourhood Snapshot",
        description: "Update the snapshot name and description. Captured neighbourhood data remains unchanged.",
        submitButtonText: 'Update',
        renderForm: SeatingPlanFormDialog,
        action: (openDialog, snapshot: NeighbourhoodSnapshot) => openDialog({
            initialValue: {name: snapshot.name, description: snapshot.description},
            onSubmit: form => updateSnapshot(snapshot, form),
            successMessage: form => `Updated snapshot ${form.name}.`,
            errorMessage: 'Failed to update the neighbourhood snapshot. Please try again later.',
        }),
    }, [updateSnapshot]);

    const handleDelete = useDeleteAction({
        description: 'Are you sure you want to delete this snapshot? This action cannot be undone.',
        action: deleteSnapshot,
        successMessage: snapshot => `Deleted snapshot ${snapshot.name}.`,
        failureMessage: 'Failed to delete the neighbourhood snapshot. Please try again later.',
    }, [deleteSnapshot]);

    const handleCreatePlan = useFormDialogAction({
        title: "Create Seating Plan from Snapshot",
        description: "Review the plan details. Desk assignments are captured by the snapshot but are not restored by seating plans.",
        submitButtonText: 'Create',
        renderForm: SeatingPlanFormDialog,
        action: (openDialog, snapshot: NeighbourhoodSnapshot) => openDialog({
            initialValue: {name: `Restore ${snapshot.name}`, description: snapshot.description},
            onSubmit: async form => {
                const plan = await createPlanFromSnapshot(snapshot, form);
                navigate(pageUrl(AppPages.seatingPlan, {planId: plan.id}));
                return plan;
            },
            successMessage: `Created seating plan from ${snapshot.name}.`,
            errorMessage: 'Failed to create a seating plan from the snapshot. Please try again later.',
        }),
    }, [createPlanFromSnapshot, navigate, pageUrl]);

    return (
        <Page
            header="Neighbourhood Snapshots"
            counter={<StorePageCount value={remoteValue}/>}
            description="Capture and restore neighbourhood configurations."
            actions={canAdmin && (
                <Button variant="contained" startIcon={<AddIcon/>} onClick={handleCreate}>
                    Create Snapshot
                </Button>
            )}
        >
            <LoadingData
                value={remoteValue}
                emptyMessage="No neighbourhood snapshots found."
                defaultErrorMessage="Failed to load neighbourhood snapshots. Please try again later."
            >
                {snapshots => (
                    <NeighbourhoodSnapshotTable
                        rows={snapshots}
                        onCreatePlan={canAdmin ? handleCreatePlan : undefined}
                        onEdit={canAdmin ? handleUpdate : undefined}
                        onDelete={canAdmin ? handleDelete : undefined}
                    />
                )}
            </LoadingData>
        </Page>
    );
}
